import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  IoDesktop,
  IoCloud,
  IoWarning,
  IoBag,
  IoMedical,
  IoServer,
  IoEgg,
  IoCart,
} from "react-icons/io5";
import { useSelector } from "react-redux";
import alarmSound from "../alarm.wav";
import { NavLink } from "react-router-dom";
import ExpiredModal from "./ExpiredModal";
import ProductGrid from "./ProductGrid";

const ShelflifeList = () => {
  const [expiredItem, setExpiredItem] = useState(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [activeTab, setActiveTab] = useState("Prep");
  const [currentTime, setCurrentTime] = useState("");
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);
  const [categoryId, setCategoryId] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedWip, setSelectedWip] = useState("");
  const [activeButton, setActiveButton] = useState({});
  const audioRefs = useRef({});
  const [mutedAlarm, setMutedAlarms] = useState({});
  const { user } = useSelector((state) => state.auth);
  const api = process.env.REACT_APP_SERVER;

  useEffect(() => {
    const expired = data.find((item) => {
      if (item.status !== "none") return false;
      const now = new Date();
      const today = now.toISOString().split("T")[0];
      if (item.endDate !== today) return false;
      const [h, m, s] = item.endShelflife.split(":").map(Number);
      const endTime = new Date();
      endTime.setHours(h, m, s, 0);
      return now >= endTime;
    });
    if (!expired) return;
    if (expiredItem?.uuid === expired.uuid) return;
    playAlarm(expired.uuid);

    setExpiredItem(expired);

    setShowExpiredModal(true);
  }, [data]);

  useEffect(() => {
    const getCategories = async () => {
      const response = await axios.get(`${api}/categories`);
      setCategories(response.data);
    };
    getCategories();
  }, []);

  useEffect(() => {
    setSelectedCategory(activeTab);
  }, [activeTab]);

  useEffect(() => {
    const getProduct = async () => {
      if (selectedCategory) {
        const response = await axios.get(
          `${api}/products/category-name?categoryName=${selectedCategory}`,
        );
        setProducts(response.data);
      }
    };
    getProduct();
  }, [selectedCategory]);

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSelectedProduct("");
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const formattedTime = now.toLocaleTimeString("en-GB", {
        hour12: false,
      });
      setCurrentTime(formattedTime);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSelectChange = (e) => {
    const selectedWipName = e.target.value;
    const selectedWipData = products.find(
      (product) => product.name === selectedWipName,
    );

    if (selectedWipData) {
      setSelectedWip(selectedWipData.name);
      setHour(selectedWipData.shelflifeInHour || 0);
      setMinute(selectedWipData.shelflifeInMinute || 0);
      setCategoryId(selectedWipData.category?.uuid || "");
    }
  };

  const createShelflife = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${api}/shelflife`, {
        name: selectedWip,
        hour: hour,
        minute: minute,
        categoryId: categoryId,
      });
      await fetchData(activeTab);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.msg);
      }
    }
  };

  const updateStatus = async (uuid, status) => {
    try {
      await axios.patch(`${api}/shelflifes/${uuid}`, { status: status });
      await fetchData(activeTab);
      console.log("status updated");
    } catch (error) {
      console.error(error.response?.data?.msg);
    }
  };

  const fetchData = async (tab) => {
    try {
      let url = "";
      switch (tab) {
        case "Prep":
          url = `${api}/shelflifes/category-name?categoryName=Prep`;
          break;
        case "Hot Display":
          url = `${api}/shelflifes/category-name?categoryName=Hot Display`;
          break;
        case "Thawing":
          url = `${api}/shelflifes/category-name?categoryName=Thawing`;
          break;
        case "Bain Marie":
          url = `${api}/shelflifes/category-name?categoryName=Bain Marie`;
          break;
        case "Suhu Ruang":
          url = `${api}/shelflifes/category-name?categoryName=Suhu Ruang`;
          break;
        case "Bar":
          url = `${api}/shelflifes/category-name?categoryName=Bar`;
          break;
        case "Open Pack":
          url = `${api}/shelflifes/category-name?categoryName=Open Pack`;
          break;
        default:
          break;
      }
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      setError("Error fetching data");
      console.error("Error fetching data:", error);
    }
  };
  useEffect(() => {
    fetchData(activeTab);

    const interval = setInterval(() => {
      fetchData(activeTab);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeTab]);

  const waste = (endDate, endShelflife, uuid) => {
    const now = new Date();
    const today = now.toISOString().split("T")[0];

    if (endDate === today) {
      const [endHour, endMinute, endSecond] = endShelflife
        .split(":")
        .map(Number);
      const endTime = new Date();
      endTime.setHours(endHour, endMinute, endSecond, 0);

      if (now >= endTime) {
        playAlarm(uuid);
        return true;
      }
    }
    return false;
  };

  const playAlarm = (uuid) => {
    if (!audioRefs.current[uuid]) {
      audioRefs.current[uuid] = new Audio(alarmSound);
    }
    audioRefs.current[uuid]
      .play()
      .then(() => {
        setActiveButton((prev) => ({ ...prev, [uuid]: true }));
      })
      .catch((error) => console.error("Alarm error", error));
  };

  const muteAlarm = (uuid) => {
    if (audioRefs.current[uuid]) {
      audioRefs.current[uuid].volume = 0;
      audioRefs.current[uuid].currentTime = 0;
      setMutedAlarms((prev) => ({
        ...prev,
        [uuid]: true,
      }));
    }
  };

  const handleMuteandWaste = (uuid) => {
    muteAlarm(uuid);
    updateStatus(uuid, "Waste");
  };

  const handleUpdateandHidden = (uuid) => {
    muteAlarm(uuid);
    updateStatus(uuid, "Habis");
  };

  return (
    <div>
      {user && user.role === "user" && <div className="mt-2"></div>}
      <div className="table-container">
        {user && user.role === "user" && (
          <div className="grid mb-1">
            <div className="card is-shadowless">
              <div className="card-content">
                <div className="content">
                  <form onSubmit={createShelflife}>
                    <p className="has-text-centered">{error}</p>
                    <div className="field">
                      <label className="label has-text-weight-bold">
                        Product Name
                      </label>
                      <ProductGrid
                        products={products}
                        selectedWip={selectedWip}
                        setSelectedWip={setSelectedWip}
                        setHour={setHour}
                        setMinute={setMinute}
                        setCategoryId={setCategoryId}
                      />
                    </div>
                    <div className="field" style={{ display: "none" }}>
                      <label className="label">Shelflife in Hour</label>
                      <div className="control">
                        <input
                          type="number"
                          className="input"
                          value={hour}
                          onChange={(e) =>
                            setHour(parseInt(e.target.value) || 0)
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="field" style={{ display: "none" }}>
                      <label className="label">Shelflife in Minute</label>
                      <div className="control">
                        <input
                          type="number"
                          className="input"
                          value={minute}
                          onChange={(e) =>
                            setMinute(parseInt(e.target.value) || 0)
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="field" style={{ display: "none" }}>
                      <label className="label">Category UUID</label>
                      <div className="control">
                        <input
                          type="text"
                          className="input"
                          value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="field">
                      <div className="control">
                        <button
                          type="submit"
                          className="button is-fullwidth"
                          style={{
                            backgroundColor: "#E53935",
                            color: "#FFFFFF",
                            fontWeight: "700",
                            borderRadius: "10px",
                            border: "none",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.35)",
                          }}
                          disabled={!selectedWip}
                        >
                          Create Shelflife
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {user && user.role === "user" && (
        <table className="table is-striped is-fullwidth is-narrow">
          <thead>
            <tr className="is-link">
              <th className="has-text-white">Product Name</th>
              <th className="has-text-white">Start Date</th>
              <th className="has-text-white">End Date</th>
              <th className="has-text-white">Start of Shelflife</th>
              <th className="has-text-white">End of Shelflife</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.filter((item) => item.status === "none").length === 0 ? (
              <tr>
                <td colSpan="7" className="has-text-centered">
                  No Shelflifes
                </td>
              </tr>
            ) : (
              data
                .filter((item) => item.status === "none")
                .map((item) => {
                  const isWaste = waste(
                    item.endDate,
                    item.endShelflife,
                    item.uuid,
                  );

                  const textColor = isWaste ? "white" : "black";

                  const rowStyle = {
                    fontWeight: "bold",
                    backgroundColor: mutedAlarm[item.uuid]
                      ? "green"
                      : isWaste
                        ? "red"
                        : "transparent",
                    color: textColor,
                  };

                  return (
                    <tr key={item.uuid} style={rowStyle}>
                      <td style={{ color: textColor }}>{item.name}</td>
                      <td style={{ color: textColor }}>{item.startDate}</td>
                      <td style={{ color: isWaste ? "white" : "red" }}>
                        {item.endDate}
                      </td>
                      <td style={{ color: textColor }}>
                        {item.startShelflife}
                      </td>
                      <td style={{ color: isWaste ? "white" : "red" }}>
                        {item.endShelflife}
                      </td>

                      <td>
                        <button
                          className="button is-small is-fullwidth has-text-weight-bold has-text-danger is-rounded"
                          onClick={() => handleUpdateandHidden(item.uuid)}
                        >
                          Habis
                        </button>
                      </td>

                      <td>
                        {activeButton[item.uuid] && (
                          <button
                            className="button is-small is-fullwidth has-text-weight-bold has-text-danger is-inverted is-rounded is-outlined"
                            onClick={() => handleMuteandWaste(item.uuid)}
                          >
                            Waste
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
      )}
      <ExpiredModal
        open={showExpiredModal}
        item={expiredItem}
        onHabis={() => {
          handleUpdateandHidden(expiredItem.uuid);
          setShowExpiredModal(false);
          setExpiredItem(null);
        }}
        onWaste={() => {
          handleMuteandWaste(expiredItem.uuid);
          setShowExpiredModal(false);
          setExpiredItem(null);
        }}
      />
      {user && user.role === "admin" && (
        <div className="grid mb-3">
          <div className="card is-shadowless">
            <div className="card-content">
              <div className="content">
                <h1>Welcome Admin</h1>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShelflifeList;
